// A staff payment link (/secure/payment/<order-id>[/<instalment-id>]): the
// order a school raised for a private event or a service, its payment plan,
// and paying the next instalment, a named one, or what's left. A port of
// v1's SecurePaymentClient + SecurePaymentView decisions. Pure.
//
// Endpoints (v1's): GET /widget/pages/secure-payment/<school-slug>/<order-id>
// (404 unless the order is that school's) and POST
// /widget/secure-payment/<order-id>/start-payment, which mints the
// PaymentIntent (or a Stripe-hosted Checkout session) on the school's
// account.

import { safeTimezone } from "../../timezones";
import { formatClock } from "../reschedule/sessions";
import { isValidDate, ordinal } from "../../dates";

export type Instalment = {
  id: string;
  name: string;
  amount: number;
  isPaid: boolean;
  dueDate?: string;
};

export type PurchasePortion = {
  _id: string;
  amount: number;
  instalmentId?: string;
  createdAt: string;
};

export type SecureOrder = {
  _id: string;
  schoolID?: string;
  userCharged: number;
  totalAmount: number;
  amountRemaining: number;
  orderType: "booking" | "product" | "giftcard" | "service";
  userMoneyStatus: "paid" | "pending" | "partial" | "refunded";
  paymentPlan: Instalment[];
  purchasePortions: PurchasePortion[];
  currencyUsed: string;
  paymentDueDate?: string;
};

/** A private event's booking (orderType "booking"). */
export type PrivateBooking = {
  school?: unknown;
  status?: string;
  buyerEmail?: string;
  privateCourse: {
    title: string;
    studentCount: number;
    price?: number;
    startDateTime: string;
    duration: number;
    address: string;
    additionalDetails?: string;
  };
};

/** A service (orderType "service"). */
export type PrivateService = { schoolID?: unknown; school?: unknown; name: string; description?: string };

export type SecurePaymentSchool = {
  _id?: string;
  name: string;
  images?: Array<{ url: string }>;
  customTerms?: { privateEvents?: unknown } | null;
  timezone?: string;
};

export type SecurePaymentPage = {
  order: SecureOrder;
  relatedItem: PrivateBooking | PrivateService | null;
  school: SecurePaymentSchool;
};

/** What start-payment answers. */
export type StartPaymentResponse = {
  finalPrice: number;
  paymentIntentClientSecret?: string;
  paymentMethodType?: string;
  schoolAccountID?: string;
  usedStripeHostedPage?: boolean;
  checkoutURL?: string;
};

/** The plan with each instalment a purchase already paid marked paid (the
 * order's own `isPaid` lags the purchase portions). */
export function mergedPlan(order: Pick<SecureOrder, "paymentPlan" | "purchasePortions">): Instalment[] {
  const paidIds = new Set((order.purchasePortions ?? []).map((p) => p.instalmentId).filter(Boolean));
  return (order.paymentPlan ?? []).map((option) => ({
    id: option.id,
    name: option.name,
    amount: option.amount,
    isPaid: option.isPaid || paidIds.has(option.id),
    dueDate: option.dueDate,
  }));
}

/** The instalment the address names: none named, the merged instalment, or
 * "missing" when it isn't in the plan (v1 answers that with a 404). */
export function instalmentOf(order: SecureOrder, instalmentId: string): Instalment | null | "missing" {
  if (!instalmentId) return null;
  return mergedPlan(order).find((option) => option.id === instalmentId) ?? "missing";
}

export const isFullyPaid = (order: Pick<SecureOrder, "userMoneyStatus">) => order.userMoneyStatus === "paid";
export const isCancelled = (order: Pick<SecureOrder, "userMoneyStatus">) => order.userMoneyStatus === "refunded";

/** "Full Amount" until something's been paid, then "Full Remaining Amount". */
export function remainingTitle(order: Pick<SecureOrder, "amountRemaining" | "totalAmount">): string {
  return order.amountRemaining === order.totalAmount ? "Full Amount" : "Full Remaining Amount";
}

/** The page's heading. */
export function orderTitle(order: Pick<SecureOrder, "orderType">, relatedItem: SecurePaymentPage["relatedItem"]): string {
  if (!relatedItem) return "";
  if (order.orderType === "booking" && "privateCourse" in relatedItem) return `Private Event: ${relatedItem.privateCourse.title}`;
  if (order.orderType === "service" && "name" in relatedItem) return relatedItem.name;
  return "";
}

/** What the page's Pay Now (the sticky bar on a phone) pays: `instalmentId`
 * undefined pays what's left. Null when there's nothing to pay. */
export type PayableOption = { name: string; amount: number; instalmentId?: string };

export function payableOption(order: SecureOrder, instalment: Instalment | null): PayableOption | null {
  if (isFullyPaid(order) || isCancelled(order)) return null;
  if (instalment) return instalment.isPaid ? null : { name: instalment.name, amount: instalment.amount, instalmentId: instalment.id };
  const nextUnpaid = mergedPlan(order).find((option) => !option.isPaid);
  if (nextUnpaid) return { name: nextUnpaid.name, amount: nextUnpaid.amount, instalmentId: nextUnpaid.id };
  if (order.amountRemaining > 0) return { name: remainingTitle(order), amount: order.amountRemaining };
  return null;
}

/** The paid-so-far bar: shown on a live order with a plan or a part payment. */
export function progressOf(order: SecureOrder): null | {
  paid: number;
  total: number;
  percent: number;
  paidInstalments?: number;
  totalInstalments?: number;
} {
  const plan = mergedPlan(order);
  if (isCancelled(order) || !(order.totalAmount > 0)) return null;
  if (plan.length === 0 && !(order.amountRemaining < order.totalAmount)) return null;
  const paid = Math.max(0, order.totalAmount - order.amountRemaining);
  const percent = Math.min(100, Math.max(0, (paid / order.totalAmount) * 100));
  return {
    paid,
    total: order.totalAmount,
    percent,
    ...(plan.length > 0 ? { paidInstalments: plan.filter((o) => o.isPaid).length, totalInstalments: plan.length } : {}),
  };
}

function zonedParts(iso: string, timezone: string | undefined, options: Intl.DateTimeFormatOptions) {
  const out: Record<string, string> = {};
  for (const part of new Intl.DateTimeFormat("en-GB", { timeZone: safeTimezone(timezone), ...options }).formatToParts(new Date(iso)))
    out[part.type] = part.value;
  return out;
}

/** "5th October 2026" (v1's "Do MMMM YYYY"), for a due date. */
export function formatDueDate(iso: string | undefined, timezone: string | undefined): string | null {
  if (!isValidDate(iso)) return null;
  const p = zonedParts(iso, timezone, { day: "numeric", month: "long", year: "numeric" });
  return `${ordinal(Number(p.day))} ${p.month} ${p.year}`;
}

/** The private event's day and times: "Monday 5th October" and
 * "14:30 - 16:30" (v1's "dddd Do MMMM", in 24-hour time). */
export function formatEventWhen(
  startDateTime: string,
  durationMinutes: number,
  timezone: string | undefined,
): { day: string; time: string } | null {
  if (!isValidDate(startDateTime)) return null;
  const p = zonedParts(startDateTime, timezone, { weekday: "long", day: "numeric", month: "long" });
  const tz = safeTimezone(timezone);
  const end = new Date(new Date(startDateTime).getTime() + (Number(durationMinutes) || 0) * 60_000).toISOString();
  return { day: `${p.weekday} ${ordinal(Number(p.day))} ${p.month}`, time: `${formatClock(startDateTime, tz)} - ${formatClock(end, tz)}` };
}

/** "x2 attendees". */
export const attendeesLabel = (count: number) => `x${count} attendee${count > 1 ? "s" : ""}`;

/** The start-payment body. Stripe's return (and a hosted Checkout's
 * success and cancel addresses, which the API builds from host, protocol
 * and path) all point back at this page on this host: `pageUrl` is its
 * absolute address with the link query. `returnURL` carries v1's `success`
 * flag: it is where the order's processing page sends the customer once
 * the payment is through. `returnPathName` is the path with the link query
 * and the cancel marker: the API builds the hosted Checkout's cancel
 * address as protocol//host + it, and keeps its query (less the marker) on
 * the success address. */
export function startPaymentBody(location: { protocol: string; host: string }, pageUrl: string, instalmentId?: string) {
  const page = new URL(pageUrl);
  return {
    instalmentId,
    returnURL: withFlag(pageUrl, "success"),
    returnHost: location.host,
    returnPathName: withFlag(`${page.pathname}${page.search}`, "baluu_checkout=cancelled"),
    returnProtocol: location.protocol,
    appCompany: "baluu",
  };
}

/** A URL with a bare flag added to its query ("…?a=1&success"). */
export function withFlag(url: string, flag: string): string {
  const hash = url.indexOf("#");
  const path = hash === -1 ? url : url.slice(0, hash);
  const fragment = hash === -1 ? "" : url.slice(hash);
  const joiner = !path.includes("?") ? "?" : /[?&]$/.test(path) ? "" : "&";
  return `${path}${joiner}${flag}${fragment}`;
}

/** What the order looked like when a payment was started, to tell when the
 * API has caught up with it. */
export type PaymentBaseline = { amountRemaining: number; instalmentId?: string };

export function baselineOf(order: SecureOrder, instalmentId?: string): PaymentBaseline {
  return { amountRemaining: order.amountRemaining, ...(instalmentId ? { instalmentId } : {}) };
}

/** Whether the order now shows the payment: paid off, less left to pay, or
 * the instalment paid. Without a baseline (a return from Stripe in another
 * tab) only a paid-off order counts. */
export function paymentLanded(order: SecureOrder, baseline: PaymentBaseline | null): boolean {
  if (isFullyPaid(order)) return true;
  if (!baseline) return false;
  if (order.amountRemaining < baseline.amountRemaining) return true;
  if (baseline.instalmentId) return mergedPlan(order).some((o) => o.id === baseline.instalmentId && o.isPaid);
  return false;
}

/** A stored baseline read back (useStripeReturn keeps it in
 * sessionStorage across a Stripe redirect), or nothing. */
export function parseBaseline(value: unknown): PaymentBaseline | null {
  const v = value as Partial<PaymentBaseline> | null;
  if (!v || typeof v !== "object" || typeof v.amountRemaining !== "number") return null;
  return { amountRemaining: v.amountRemaining, ...(typeof v.instalmentId === "string" ? { instalmentId: v.instalmentId } : {}) };
}
